import { type ChangeEventHandler, type ForwardedRef, forwardRef } from 'react'
import { Box, type BoxProps } from '../Box'
import { visuallyHidden } from '../util.css'
import {
  radioInputStyle,
  radioInputBase,
  mediumRadioInput,
  smallRadioInput,
} from './Checkbox.css'
import { Label } from './Input'
import { inputBase } from './Input.css'

const inputSizes = {
  small: smallRadioInput,
  medium: mediumRadioInput,
} as const

interface InputProps {
  onChange?: ChangeEventHandler<HTMLInputElement>
  unstyled?: boolean
  unspaced?: boolean
  size?: keyof typeof inputSizes
  type?: 'checkbox' | 'radio'
  name: string
  id: string
  inputClassName?: BoxProps['className']
  // TODO: remove refToLabel once tooltip is refactored so we dont have to deal with this. without this ref goes to input (correct mostly) which is absolute and hidden and tooltip renders to top left corner
  refToLabel?: boolean
}

// Omit onClick because inputs should use onChange. Using onClick is considered misusage
type Props = Omit<BoxProps, keyof InputProps | 'type' | 'onClick'> & InputProps

/** @deprecated */
export type { Props as CheckboxProps }

/** @deprecated */
export const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      type = 'checkbox',
      size = 'medium',
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      name,
      id,
      checked,
      defaultChecked,
      value,
      children,
      className,
      unstyled,
      unspaced,
      disabled,
      inputClassName,
      refToLabel,
      required,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        <Box
          as="input"
          type={type}
          textAlign="inherit"
          ref={refToLabel ? undefined : ref}
          className={[
            { [visuallyHidden]: !unstyled },
            radioInputBase,
            inputClassName,
          ]}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          disabled={disabled}
          id={id}
          name={name}
          checked={checked}
          defaultChecked={defaultChecked}
          value={value}
          required={required}
          aria-invalid={ariaInvalid}
        />
        <Label
          display="flex"
          paddingY="xxs"
          justifyContent="flexStart"
          position="relative"
          alignItems="center"
          gridColumnGap="xs"
          aria-disabled={disabled ? true : undefined}
          gap="xxs"
          htmlFor={id}
          ref={refToLabel ? (ref as ForwardedRef<HTMLLabelElement>) : undefined}
          cursor="pointer"
          className={[
            className,
            inputBase,
            {
              [inputSizes[size]]: !unspaced,
              [radioInputStyle]: !unstyled,
            },
          ]}
          {...props}
        >
          {children}
        </Label>
      </>
    )
  },
)

Checkbox.displayName = 'Checkbox'
